import { createI18n } from 'vue-i18n'
import { i18nOptimizer, measureI18nPerformance } from './i18n-optimizer'

// Configuration constants
const LANG_PATH = window.location.protocol + '//' + window.location.host + '/lang/'
const CACHE_BUSTER = '?v=' + (process.env.NODE_ENV === 'production' ? '1.0.0' : Date.now())
const DEFAULT_LOCALE = 'en'
const SUPPORTED_LOCALES = ['en', 'zh_cn', 'ms'] as const

// Get user's preferred language
const getUserLocale = (): string => {
    const stored = localStorage.getItem('lang')
    if (stored && SUPPORTED_LOCALES.includes(stored as any)) {
        return stored
    }
    
    // Fallback to browser language if supported
    const browserLang = navigator.language.split('-')[0]
    if (SUPPORTED_LOCALES.includes(browserLang as any)) {
        return browserLang
    }
    
    return DEFAULT_LOCALE
}

// Optimized CSV parsing function with performance measurement
const parseCSVData = (data: string): Record<string, string> => {
    return measureI18nPerformance('CSV parsing', () => {
        try {
            const messages: Record<string, string> = {}
            
            // Use regex to properly parse CSV with quoted strings
            const lines = data.split('\n')
            for (const line of lines) {
                if (line.trim()) {
                    // Regex to match: "key","value" where value can contain commas
                    // This handles the CSV format: "login_title_desc","A simple, fast and secure way to sign documents online."
                    const match = line.match(/^"([^"]+)","([^"]*)"$/)
                    if (match) {
                        const key = match[1].trim()
                        const value = match[2]
                        if (key) {
                            messages[key] = value
                        }
                    }
                }
            }
            
            // Debug: Log first few messages to verify structure
            /* if (process.env.NODE_ENV === 'development') {
                const sampleKeys = Object.keys(messages).slice(0, 5)
                console.log(`Parsed ${Object.keys(messages).length} messages for locale`)
                console.log('Sample keys:', sampleKeys)
                if (sampleKeys.length > 0) {
                    console.log('Sample message structure:', messages[sampleKeys[0]])
                    // Check specifically for the problematic key
                    if (messages['all_btn_login']) {
                        console.log('Found all_btn_login:', messages['all_btn_login'])
                    } else {
                        console.log('MISSING: all_btn_login key not found!')
                        // Log all keys that contain 'login' for debugging
                        const loginKeys = Object.keys(messages).filter(k => k.includes('login'))
                        console.log('Keys containing "login":', loginKeys)
                    }
                    
                    // Check for the specific key mentioned in the issue
                    if (messages['login_title_desc']) {
                        console.log('Found login_title_desc:', messages['login_title_desc'])
                        console.log('Length:', messages['login_title_desc'].length)
                        console.log('Full text:', JSON.stringify(messages['login_title_desc']))
                        
                        // Test if the text contains the expected content
                        if (messages['login_title_desc'].includes('fast and secure')) {
                            console.log('Text contains expected content: "fast and secure"')
                        } else {
                            console.log('Text missing expected content: "fast and secure"')
                        }
                    } else {
                        console.log('MISSING: login_title_desc key not found!')
                    }
                }
            } */
            
            return messages
        } catch (error) {
            console.error('CSV parsing error:', error)
            return {}
        }
    })
}

// Language loading function with caching and error handling
const loadLanguage = async (locale: string): Promise<Record<string, string>> => {
    // Check cache first
    const cached = i18nOptimizer.getCachedLanguage(locale)
    if (cached) {
        console.log(`Using cached language data for ${locale}`)
        return cached
    }
    
    try {
        const response = await fetch(`${LANG_PATH}${locale}.csv${CACHE_BUSTER}`)
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        
        const data = await response.text()
        const messages = parseCSVData(data)
        
        // Cache the loaded messages
        i18nOptimizer.cacheLanguage(locale, messages)
        
        return messages
    } catch (error) {
        console.error(`Failed to load ${locale}.csv:`, error)
        return {}
    }
}

// Load all languages in parallel for better performance
const loadAllLanguages = async (): Promise<Record<string, any>> => {
    return measureI18nPerformance('language loading', async () => {
        const userLocale = getUserLocale()
        
        try {
            // Load all languages in parallel using Promise.all
            const languagePromises = SUPPORTED_LOCALES.map(async (locale) => {
                const messages = await loadLanguage(locale)
                return { locale, messages }
            })
            
            const results = await Promise.all(languagePromises)
            
            // Build messages object - Fix structure to match existing Vue component usage
            const messages: Record<string, any> = {}
            results.forEach(({ locale, messages: localeMessages }) => {
                // Structure: { locale: { message: { key: value } } }
                // This matches the existing usage pattern: $t('message.all_btn_login')
                messages[locale] = { message: localeMessages }
            })
            
            return messages
        } catch (error) {
            console.error('Failed to load languages:', error)
            
            // Fallback: try to load at least the default language
            try {
                const defaultMessages = await loadLanguage(DEFAULT_LOCALE)
                return {
                    [DEFAULT_LOCALE]: { message: defaultMessages }
                }
            } catch (fallbackError) {
                console.error('Failed to load fallback language:', fallbackError)
                return {}
            }
        }
    })
}

// Initialize i18n with optimized loading
const initializeI18n = async () => {
    const messages = await loadAllLanguages()
    
    const i18n = createI18n({
        legacy: false,
        locale: getUserLocale(),
        fallbackLocale: DEFAULT_LOCALE,
        messages,
        globalInjection: true,
        silentTranslationWarn: process.env.NODE_ENV === 'production',
        silentFallbackWarn: process.env.NODE_ENV === 'production',
    })
    
    return i18n
}

// Create and export i18n instance
let i18nInstance: any = null

const getI18n = async () => {
    if (!i18nInstance) {
        i18nInstance = await initializeI18n()
    }
    return i18nInstance
}

// Export both the async getter and a default export for backward compatibility
export { getI18n }



// For immediate use, create a basic instance that will be updated
const i18n = createI18n({
    legacy: false,
    locale: getUserLocale(),
    fallbackLocale: DEFAULT_LOCALE,
    messages: {},
    globalInjection: true,
})

// Update the instance once languages are loaded
loadAllLanguages().then(messages => {
    // Debug: Log the message structure being created
    /* if (process.env.NODE_ENV === 'development') {
        console.log('Loading languages into i18n instance...')
        
        console.log('Messages structure:', messages)
        
         // Check if the structure is correct
        if (messages.en && messages.en.message) {
            const sampleKey = Object.keys(messages.en.message)[0]
            console.log(`Sample key: ${sampleKey}`)
            console.log(`Sample value: ${messages.en.message[sampleKey]}`)
            console.log(`Expected access pattern: $t('message.${sampleKey}')`)
            
             // Verify the specific key that's causing issues
            if (messages.en.message['all_btn_login']) {
                console.log('all_btn_login found in messages:', messages.en.message['all_btn_login'])
            } else {
                console.log('all_btn_login NOT found in messages!')
                console.log('Available keys (first 10):', Object.keys(messages.en.message).slice(0, 10))
            } 
        }
    } */
    
    // Ensure we have the correct message structure
    const enMessages = messages.en || { message: {} }
    const zhMessages = messages.zh_cn || { message: {} }
    const msMessages = messages.ms || { message: {} }
    
    // Set the messages in the i18n instance
    i18n.global.setLocaleMessage('en', enMessages)
    i18n.global.setLocaleMessage('zh_cn', zhMessages)
    i18n.global.setLocaleMessage('ms', msMessages)
    
    // Log performance metrics
    const metrics = i18nOptimizer.getPerformanceMetrics()
    // Debug: console.log('i18n Performance Metrics:', metrics)
    
    // Verify the final structure
    /* if (process.env.NODE_ENV === 'development') {
        console.log('Final i18n message structure:')
        const finalEnMessages = i18n.global.getLocaleMessage('en')
        console.log('en messages:', finalEnMessages)
        console.log('Available locales:', i18n.global.availableLocales)
        
        // Test if we can access the problematic key
        try {
            const testKey = 'message.all_btn_login'
            const testResult = i18n.global.t(testKey)
            console.log(`Test access to ${testKey}:`, testResult)
        } catch (error) {
            console.error('Error accessing test key:', error)
        }
    } */
}).catch(error => {
    console.error('Failed to initialize i18n:', error)
})

// Clean up expired cache entries periodically
setInterval(() => {
    i18nOptimizer.clearExpiredCache()
}, 60 * 60 * 1000) // Every hour

export default i18n


/* export async function loadLocaleMessages(locale: string) {
  console.info('- - loadLocaleMessages', locale, 'LS', lang)
  const messages = await import(`./${locale}.json`)
  i18n.global.setLocaleMessage(locale, messages.default)
  i18n.global.locale = locale
} */

// legacy: false, // Use Composition API mode
// fallbackLocale: 'en', 

// working


/* const i18n = createI18n({
    locale: "" + lang + "",
    messages: {
      en: {
        signUp: "Sign Up en!!",
        name: "Name",
        LANG_ADDRESS: "Address",
        L_LOGIN: "Login",
        L_FOLDER: "Folder",
        SUPPORT_LBL_DISPLAY_NAME: "Display name"
      },
      bm: {
        signUp: "Register sini!!",
        name: "Nama",
        L_LOGIN: "Log masuk",
        LANG_ADDRESS: "Alamat",
        L_FOLDER: "Fail",
        SUPPORT_LBL_DISPLAY_NAME: "Nama"
      },
      ch: {
        signUp: "账户注册",
        name: "姓名",
        LANG_ADDRESS: "地址",
        L_LOGIN: "登录",
        L_FOLDER: "文件夹",
        SUPPORT_LBL_DISPLAY_NAME: "显示名称"        
      }
    }
}) */

// export default i18n

